import { z } from 'zod';
import { dateOnlySchema } from './common';
/**
 * Exports of services: the GST rules an export invoice has to satisfy, and the
 * FEMA obligations that follow it until the money is home.
 *
 * Every rule here was read in the primary text on 2026-10-11 (requests.md
 * R-01 and R-08 carry the quotes and the URLs). Where a value is our reading
 * rather than the text, the comment says so.
 */
// ── The Letter of Undertaking ─────────────────────────────────────────────
/**
 * An LUT, filed in GST RFD-11 for one Indian financial year (CGST Rule 96A,
 * "deemed to be accepted" on the ARN). Exports under it are zero-rated
 * without payment of IGST.
 *
 * Keyed by financial year, not a typed date range: an LUT is always April to
 * March whatever the book's own fiscal year, and two dates a person types are
 * two chances to type one wrong.
 */
export const financialYearSchema = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}$/, 'Write the financial year as 2026-27')
    .refine((fy) => (Number(fy.slice(0, 4)) + 1) % 100 === Number(fy.slice(5, 7)), {
    message: 'The two halves of a financial year are consecutive years, e.g. 2026-27',
});
export const lutSchema = z
    .object({
    /** The acknowledgement (ARN) the portal issued, e.g. AD330326012345X. */
    arn: z.string().trim().toUpperCase().min(5, 'Enter the ARN from the portal').max(30),
    financialYear: financialYearSchema,
    /** When it was filed. Optional: the ARN is the proof, the date is a nicety. */
    filedOn: dateOnlySchema.optional(),
})
    .strict();
export const lutsSchema = z
    .array(lutSchema)
    .max(30)
    .superRefine((luts, ctx) => {
    const seen = new Set();
    luts.forEach((lut, index) => {
        if (seen.has(lut.financialYear)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, 'financialYear'],
                message: `There is already an LUT for ${lut.financialYear}. One per financial year.`,
            });
        }
        seen.add(lut.financialYear);
    });
});
/** The Indian financial year a date falls in: 2026-09-23 → "2026-27", 2027-02-01 → "2026-27". */
export function financialYearOf(date) {
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    const start = month >= 4 ? year : year - 1;
    return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}
/** The LUT covering a date, or null when none is on file for its financial year. */
export function lutFor(luts, date) {
    const fy = financialYearOf(date);
    return luts?.find((lut) => lut.financialYear === fy) ?? null;
}
// ── What the invoice must say (CGST Rule 46, second proviso) ─────────────
/**
 * The endorsement, VERBATIM from the current Rule 46 on CBIC's site. An
 * inspector looks for the sentence, not the sentiment: the paraphrase Fint
 * printed before ("Zero-rated supply: export / SEZ supply under LUT…") was
 * neither of these.
 */
export const EXPORT_ENDORSEMENT_UNDER_LUT = 'SUPPLY MEANT FOR EXPORT/SUPPLY TO SEZ UNIT OR SEZ DEVELOPER FOR AUTHORISED OPERATIONS UNDER BOND OR LETTER OF UNDERTAKING WITHOUT PAYMENT OF INTEGRATED TAX';
export const EXPORT_ENDORSEMENT_ON_PAYMENT = 'SUPPLY MEANT FOR EXPORT/SUPPLY TO SEZ UNIT OR SEZ DEVELOPER FOR AUTHORISED OPERATIONS ON PAYMENT OF INTEGRATED TAX';
/**
 * The place of supply of an export, as the GST system codes it: "96 OTHER
 * COUNTRIES" in the e-invoice master codes. Deliberately NOT in
 * INDIAN_STATES - that list is also where a book picks the state it is
 * registered in, and "Other Countries" is not a state anyone registers in.
 */
export const EXPORT_PLACE_OF_SUPPLY = { code: '96', name: 'Other Countries' };
// ── FEMA: the declaration and the money (FEMA 23(R)/2026-RB) ──────────────
/**
 * The 2026 Regulations' commencement (Reg 1(2)). They carry no transition
 * clause, so an invoice dated before this is under the 2015 Regulations and
 * no EDF is due for it under the new Reg 3(2).
 */
export const FEMA_2026_FROM = '2026-10-01';
/**
 * The month an export invoice is declared in: Reg 3(2)(a) lets one EDF cover
 * every export invoiced in a month. Null for an invoice the 2026 rule does
 * not reach.
 */
export function edfMonthOf(invoiceDate) {
    return invoiceDate >= FEMA_2026_FROM ? invoiceDate.slice(0, 7) : null;
}
/**
 * When the EDF for a month is due: "within 30 days from the end of month in
 * which invoice for services has been raised" (Reg 3(2)). Thirty days from
 * the last day, so October's is 30 November and January's is 2 March - not
 * "the end of the next month".
 */
export function edfDueDate(month) {
    const year = Number(month.slice(0, 4));
    const monthNumber = Number(month.slice(5, 7));
    // Day 0 of the next month is the last day of this one, so day 30 of the
    // next month is that last day plus thirty. Date.UTC rolls the overflow.
    return isoOf(new Date(Date.UTC(year, monthNumber, 30)));
}
/**
 * When the export value must be realised: nine months from the invoice date
 * for services, twelve when invoiced or settled in INR (Reg 5(1)(a), as
 * amended by FEMA 23(R)/(1)/2026-RB on 22-Sep-2026; the 2015 Regulations also
 * gave nine). The AD bank may extend it; the extension is recorded on the
 * invoice, not computed.
 */
export function realisationDueDate(invoiceDate, settledInInr) {
    return addMonths(invoiceDate, settledInInr ? 12 : 9);
}
/**
 * CGST Rule 96A(1)(b), as substituted in July 2024: if the payment is not
 * received within one year or the FEMA period, whichever is LATER, the IGST
 * is payable with interest within fifteen days after it.
 */
export function rule96aDueDate(invoiceDate, femaDue) {
    const oneYear = addMonths(invoiceDate, 12);
    return addDaysIso(oneYear > femaDue ? oneYear : femaDue, 15);
}
/**
 * Is this SAC "software" within Reg 2(1)(e) ("any computer programme,
 * database, drawing, design … in or on any medium other than in or on any
 * physical medium")? Software exporters do NOT get Reg 3(2)(b), the option
 * to file the EDF at payment instead.
 *
 * OUR READING, not the text: the Regulations name no SAC codes. IT design,
 * development, consulting, hosting and software downloads/licences are
 * treated as software; anything else is not. A book that disagrees still
 * gets the same 30-day date - the difference is only that a non-software
 * exporter is told the later option exists.
 */
export function isSoftwareSac(sac) {
    const code = (sac ?? '').replace(/\s+/g, '');
    return /^99831[3-9]/.test(code) || /^998434/.test(code) || /^997331/.test(code);
}
/**
 * Closing the EDPMS entry on the exporter's own declaration is allowed up to
 * ₹10 lakh per services invoice, quarterly in bulk (Reg 4(2), first proviso).
 */
export const EDPMS_SELF_DECLARATION_LIMIT_INR = '1000000.00';
export const EDF_AUTHORITIES = ['ad_bank', 'stpi'];
export const EDF_AUTHORITY_LABELS = {
    ad_bank: 'Authorised Dealer bank',
    stpi: 'STPI',
};
export const EDPMS_CLOSURE_ROUTES = ['bank', 'self_declaration'];
export const EDPMS_CLOSURE_ROUTE_LABELS = {
    bank: 'Closed by the bank on realisation',
    self_declaration: 'Closed on our declaration (up to ₹10 lakh)',
};
const monthSchema = z
    .string()
    .trim()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A month is written 2026-10');
/** Recording the EDF for a month: when it went in, to whom, and the bank's acknowledgement. */
export const recordEdfSchema = z
    .object({
    filedOn: dateOnlySchema,
    authority: z.enum(EDF_AUTHORITIES).default('ad_bank'),
    /** The bank's acknowledgement or EDPMS reference - what proves the date. */
    reference: z.string().trim().max(120).optional(),
    /** A later date the bank allowed under Reg 3(2)(c), when one was asked for. */
    extendedTo: dateOnlySchema.optional(),
    note: z.string().trim().max(500).optional(),
})
    .strict();
export const edfMonthParamSchema = monthSchema;
/** What happened to one export invoice's EDPMS entry, and any extension the bank gave. */
export const recordExportClosureSchema = z
    .object({
    closedOn: dateOnlySchema.nullable().optional(),
    route: z.enum(EDPMS_CLOSURE_ROUTES).nullable().optional(),
    /** A realisation date the AD bank extended to (Reg 5). */
    realisationExtendedTo: dateOnlySchema.nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
})
    .strict()
    .refine((v) => !v.closedOn || !!v.route, {
    message: 'Say how the entry was closed - by the bank, or on your declaration.',
    path: ['route'],
});
export const exportsQuerySchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
})
    .strict()
    .refine((v) => v.from <= v.to, { message: 'The period ends before it starts', path: ['to'] });
// ── date helpers (pure, UTC, never `new Date(localString)`) ───────────────
function isoOf(d) {
    return d.toISOString().slice(0, 10);
}
function addDaysIso(date, days) {
    const d = new Date(`${date}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return isoOf(d);
}
/** Calendar months, clamped: 31 May + 9 months is 28/29 February, never 2/3 March. */
export function addMonths(date, months) {
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7)) - 1 + months;
    const day = Number(date.slice(8, 10));
    const targetYear = year + Math.floor(month / 12);
    const targetMonth = ((month % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    return isoOf(new Date(Date.UTC(targetYear, targetMonth, Math.min(day, lastDay))));
}
//# sourceMappingURL=exports.js.map